import React, { useEffect, useRef } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Overlay shell used by the listing form (side panel) and the confirm dialogs
 * (centered). Sits above the topbar (z-index 100). ESC closes, focus moves in
 * on open, is kept inside while open, and returns to the opener on close.
 */
export default function Panel({ open, onClose, title, subtitle, children, footer, variant = "side", labelId, busy = false }) {
  const ref = useRef(null);
  const prevFocus = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const busyRef = useRef(busy);
  busyRef.current = busy;

  useEffect(() => {
    if (!open) return undefined;
    prevFocus.current = document.activeElement;
    const t = window.setTimeout(() => {
      const first = ref.current && ref.current.querySelector(FOCUSABLE);
      if (first) first.focus();
    }, 0);
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        if (!busyRef.current && onCloseRef.current) onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !ref.current) return;
      const nodes = ref.current.querySelectorAll(FOCUSABLE);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("keydown", onKey);
      const p = prevFocus.current;
      if (p && typeof p.focus === "function") {
        try { p.focus(); } catch (_) { /* element may be gone */ }
      }
    };
  }, [open]);

  if (!open) return null;

  const id = labelId || "tl-panel-title";
  const side = variant === "side";

  return (
    <div className="fixed inset-0 z-[1000]">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !busy && onClose && onClose()} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        className={
          side
            ? "absolute top-0 right-0 h-full w-full max-w-3xl overflow-y-auto flex flex-col"
            : "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(32rem,calc(100vw-2rem))] max-h-[90vh] overflow-y-auto rounded-xl flex flex-col"
        }
        style={{
          backgroundColor: "var(--bl-bg)",
          border: side ? undefined : "1px solid var(--bl-line-strong)",
          borderLeft: side ? "1px solid var(--bl-line)" : undefined,
          boxShadow: side ? "-12px 0 40px rgba(0,0,0,0.5)" : "0 24px 60px rgba(0,0,0,0.5)",
        }}
      >
        <div
          className="sticky top-0 z-10 px-6 py-4 flex items-start justify-between gap-3"
          style={{ background: "rgba(14, 13, 41, 0.92)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)", borderBottom: "1px solid var(--bl-line)" }}
        >
          <div className="min-w-0">
            <h3 id={id} className="text-lg font-medium text-gray-100 break-words">{title}</h3>
            {subtitle && <div className="text-xs text-gray-400 mt-1">{subtitle}</div>}
          </div>
          <button
            type="button"
            onClick={() => !busy && onClose && onClose()}
            className="text-gray-400 hover:text-gray-100 hover:bg-white/5 rounded-lg p-2 transition-colors shrink-0"
            aria-label="Close"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        <div className="px-6 py-5 flex-1 space-y-5">{children}</div>
        {footer && (
          <div
            className="sticky bottom-0 px-6 py-4 flex items-center justify-end gap-2 flex-wrap"
            style={{ background: "rgba(14, 13, 41, 0.92)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)", borderTop: "1px solid var(--bl-line)" }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export const btnPrimary =
  "px-4 py-2 text-sm font-medium bg-bl-accent text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-50";
export const btnGhost =
  "px-3 py-2 text-sm font-medium text-gray-200 border border-white/10 hover:bg-white/[0.04] hover:border-white/20 rounded-md transition-colors disabled:opacity-50";
export const btnDanger =
  "px-3 py-2 text-sm font-medium text-red-300 border border-red-500/30 hover:bg-red-500/10 rounded-md transition-colors disabled:opacity-50";
export const inputCls =
  "w-full px-3 py-2 text-sm border border-white/10 rounded-md bg-white/[0.03] text-gray-100 focus:outline-none focus:ring-1 focus:ring-bl-accent/60 focus:border-bl-accent/60 disabled:opacity-60";
export const labelCls = "block text-[0.7rem] font-medium text-gray-400 uppercase tracking-wider mb-1.5";
export const sectionCls = "bl-glass p-5";
export const sectionTitleCls = "text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4";
